import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { TransactionStatus, TransactionTypesOptions, withStyles, Card, Box, formatAmount } from 'vopay';
import { Title, UtilButton, StatusIcon } from 'UI';
import { useHistory } from 'react-router-dom';
import { format } from 'date-fns';
import styles from './TransactionCard.module.scss';

type TransactionCardProps = {
  receiverName: string;
  amount: string;
  currency: string;
  transactionId: string;
  parentTransactionId?: string;
  status: TransactionStatus;
  type: TransactionTypesOptions;
  transactionDate: string;
  lastModified: string;
  failureReason: string;
  transferInformation?: string;
  isApprovalRequested?: boolean;
};

const cn = withStyles(styles);

const TransactionCard: FC<TransactionCardProps> = ({
  receiverName,
  amount,
  currency,
  transactionId,
  parentTransactionId,
  status,
  type,
  transactionDate,
  lastModified,
  failureReason,
  transferInformation,
  isApprovalRequested = false,
}) => {
  const history = useHistory();
  const { t } = useTranslation('translations', {
    keyPrefix: 'Transaction.TransactionHistory.Card',
  });
  const { t: tType } = useTranslation('translations', {
    keyPrefix: 'Transaction.TransactionHistory.transactionTypes',
  });
  const { t: tStatus } = useTranslation('translations', {
    keyPrefix: 'Transaction.TransactionHistory.statuses',
  });
  const isWithdrawal = type.toLowerCase().includes('withdrawal');
  const isFunding = type.toLowerCase().includes('funding');
  const isPendingLike = ['pending', 'pending approval', 'requested', 'sent'].includes(status);
  const isPendingApproval = isApprovalRequested && status === 'pending approval';
  const isTransactionSuccessful = ['successful', 'complete'].includes(status);
  const hasTransactionEnded = [
    'successful',
    'complete',
    'failed',
    'cancelled',
    'settled',
    'declined',
    'complete',
  ].includes(status);

  const accountTransferSender = transferInformation?.match(/from\s+([\w\d_]+)/i)?.[1] ?? '';
  const accountTransferReceiver = transferInformation?.match(/to\s+([\w\d_]+)/i)?.[1] ?? '';
  const transactionType = ['Account Transfer', 'Reversal'].includes(type)
    ? tType(type, { defaultValue: type })
    : t('transaction');
  const isApprovalRejected = isApprovalRequested && ['failed', 'cancelled', 'declined'].includes(status);

  return (
    <Card className={cn('card')}>
      <Box jc="sb" gapSize="24px">
        <Box flx="col" gapSize="24px">
          <Title level="2">
            {type === 'Account Transfer' ? (
              <>
                {accountTransferSender} <span className={cn('grey')}>{t('to', { defaultValue: 'to' })}</span>{' '}
                {accountTransferReceiver}
              </>
            ) : (
              receiverName
            )}
          </Title>
          <Box flx="col" gapSize="32px">
            <Box flx="col" gapSize="5px">
              <Title level="5" emphasized={false} className={cn('grey')}>
                {isWithdrawal
                  ? t('amountSent', { defaultValue: 'Amount Sent' })
                  : isFunding
                    ? t('amountRequested', { defaultValue: 'Amount Requested' })
                    : t('amount', { defaultValue: 'Amount' })}
              </Title>
              <Title level="4">
                {currency} {formatAmount(amount)}
              </Title>
            </Box>

            <div className={cn('idContainer')}>
              <Box flx="col" gapSize="6px">
                <div className={cn('grey', 'row')}>{t('transactionId', { defaultValue: 'Transaction ID' })}</div>
                <div className={cn('fontWeight500')}>{transactionId}</div>
              </Box>
              {parentTransactionId && (
                <div className={cn('second')}>
                  <div className={cn('grey', 'row')}>
                    {t('parentTransactionId', { defaultValue: 'Parent Transaction ID' })}
                  </div>
                  <UtilButton
                    onClick={() => {
                      history.push(`/transactions-history/details/eLinx/${parentTransactionId}/${currency}`);
                    }}
                  >
                    {parentTransactionId}
                  </UtilButton>
                </div>
              )}
            </div>
          </Box>
        </Box>
        {status !== '' && (
          <div className={cn('statusContainer')}>
            {/* Step 1: Approval Pending */}
            {isApprovalRequested && ['pending', 'requested', 'successful'].includes(status) && (
              <div className={cn('status', 'border', isTransactionSuccessful && 'green')}>
                <div className={cn('icon', 'small')}>
                  <StatusIcon status="successful" size="small" />
                </div>
                <Title level="5" className={cn('grey')}>
                  {tStatus('pending approval', { defaultValue: 'Pending Approval' })}
                </Title>
              </div>
            )}

            {/* Step 2: Type-specific / Pending Status */}
            {!['Reversal', 'Account Transfer', 'Credit Card'].includes(type) && (
              <div className={cn('status', 'border', isTransactionSuccessful && 'green', isApprovalRejected && 'red')}>
                <div className={cn('icon', !isPendingLike && !isApprovalRejected && 'small')}>
                  <StatusIcon
                    status={isPendingLike ? 'pending' : isApprovalRejected ? 'approval-rejected' : 'successful'}
                    size={isPendingLike || isApprovalRejected ? 'medium' : 'small'}
                  />
                </div>
                <Box flx="col">
                  <Title
                    level={isPendingLike ? '4' : '5'}
                    className={cn(['pending', 'pending approval'].includes(status) ? 'blue' : 'grey', 'capitalize')}
                  >
                    {isPendingApproval || isApprovalRejected
                      ? tStatus('pending approval', { defaultValue: 'pending approval' })
                      : type === 'Interac Money Request' || type === 'Interac Bulk Payout'
                        ? tStatus('requested', { defaultValue: 'requested' })
                        : tStatus('pending', { defaultValue: 'pending' })}
                  </Title>
                  {isPendingLike && (
                    <>
                      <Title level="6" emphasized={false} className={cn('subTitle', 'grey')}>
                        {isPendingApproval && status === 'pending approval'
                          ? t('waitingApproval', { defaultValue: 'This transaction is awaiting approval.' })
                          : isWithdrawal
                            ? t('paymentSent', { receiverName: receiverName })
                            : t('paymentRequested', { receiverName: receiverName })}
                      </Title>
                      <div className={cn('text')}>
                        {transactionDate && format(transactionDate, 'MMM dd yyyy, HH:mm:ss')}
                      </div>
                    </>
                  )}
                </Box>
              </div>
            )}
            {/* Step 3: Final Status */}
            <div className={cn('status', isPendingLike && 'fade')}>
              <div className={cn('icon', isPendingLike && 'small')}>
                <StatusIcon
                  status={
                    ['failed', 'cancelled'].includes(status)
                      ? 'failed'
                      : ['successful', 'complete'].includes(status)
                        ? 'successful'
                        : 'pending'
                  }
                  size={isPendingLike ? 'small' : 'medium'}
                />
              </div>
              <Box flx="col">
                <Title level={isPendingLike ? '5' : '4'} className={cn('capitalize')}>
                  {isPendingLike
                    ? isApprovalRequested
                      ? t('approved', { defaultValue: 'Approved' })
                      : tStatus('successful', { defaultValue: 'successful' })
                    : tStatus(status, { defaultValue: status })}
                </Title>
                {status !== 'pending' && (
                  <>
                    <Title level="6" emphasized={false} className={cn('subTitle', 'grey')}>
                      {['successful', 'complete'].includes(status) ? (
                        <>{t('completedSuccessfully', { transactionType })}</>
                      ) : status === 'failed' ? (
                        failureReason ? (
                          failureReason
                        ) : (
                          t('transactionFailed', { defaultValue: 'Transaction has failed' })
                        )
                      ) : status === 'cancelled' ? (
                        t('transactionCancelled', { defaultValue: 'Transaction Cancelled' })
                      ) : (
                        ''
                      )}
                    </Title>
                    {hasTransactionEnded && (
                      <div className={cn('text')}>{lastModified && format(lastModified, 'MMM dd yyyy, HH:mm:ss')}</div>
                    )}
                  </>
                )}
              </Box>
            </div>
          </div>
        )}
      </Box>
    </Card>
  );
};

export default TransactionCard;
